import { isUninstalledIos } from "./platform";
import { supabase } from "./supabase";

/**
 * Web push subscription, from the browser's side.
 *
 * The subscription is stored in `push_subscriptions`; the send-push Edge
 * Function delivers to it using the VAPID private key, which never leaves
 * Supabase. The public key below is meant to be public.
 *
 * iOS caveat worth knowing before anyone reports a bug: Safari only supports
 * web push when the app has been installed to the home screen (iOS 16.4+).
 * From a browser tab, `Notification` exists but a subscription will fail.
 */

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as
  string | undefined;

export type PushState =
  "unsupported" | "needs-install" | "denied" | "unconfigured" | "off" | "on";

export async function getPushState(): Promise<PushState> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return isUninstalledIos() ? "needs-install" : "unsupported";
  }
  if (!VAPID_PUBLIC_KEY) return "unconfigured";
  if (Notification.permission === "denied") return "denied";

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  return subscription ? "on" : "off";
}

/** Ask for permission, subscribe, and record it against this user. */
export async function enablePush(profileId: string): Promise<PushState> {
  if (!VAPID_PUBLIC_KEY) return "unconfigured";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";

  await subscribeAndRecord(profileId);
  return "on";
}

/**
 * Where boot/asset-recovery.js writes the endpoint of a subscription it is
 * about to drop. Unregistering a service worker takes its push subscription
 * with it, so recovery would otherwise leave this device silent with a dead
 * row in push_subscriptions (issue #569).
 */
export const DROPPED_PUSH_KEY = "roof-hawk:push-dropped";

/**
 * Put back a subscription that asset recovery dropped. Nothing happens unless
 * recovery left a note, so a device whose owner turned notifications off stays
 * off. The dead row goes either way; the device is subscribed again only while
 * permission is still granted, which needs no prompt.
 */
export async function restoreDroppedPush(profileId: string): Promise<void> {
  let dropped: string | null = null;
  try {
    dropped = localStorage.getItem(DROPPED_PUSH_KEY);
  } catch {
    return;
  }
  if (!dropped) return;

  const { error } = await supabase
    .from("push_subscriptions")
    .delete()
    .eq("endpoint", dropped);
  if (error) throw new Error(error.message);

  if (
    VAPID_PUBLIC_KEY &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    Notification.permission === "granted"
  ) {
    await subscribeAndRecord(profileId);
  }
  localStorage.removeItem(DROPPED_PUSH_KEY);
}

async function subscribeAndRecord(profileId: string): Promise<void> {
  if (!VAPID_PUBLIC_KEY) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(
      VAPID_PUBLIC_KEY,
    ) as BufferSource,
  });

  const json = subscription.toJSON();
  const { error } = await supabase.from("push_subscriptions").upsert(
    {
      profile_id: profileId,
      endpoint: subscription.endpoint,
      p256dh: json.keys?.p256dh ?? "",
      auth: json.keys?.auth ?? "",
      user_agent: navigator.userAgent,
      last_used_at: new Date().toISOString(),
    },
    { onConflict: "endpoint" },
  );
  if (error) throw new Error(error.message);
}

export async function disablePush(): Promise<PushState> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return "off";

  await supabase
    .from("push_subscriptions")
    .delete()
    .eq("endpoint", subscription.endpoint);
  await subscription.unsubscribe();
  return "off";
}

/** VAPID keys are base64url; PushManager wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalised);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}
